import { useEffect, type CSSProperties, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { SITE } from "../config/site";
import { EXAM_BY_ID } from "../data/exams";
import type { ExamId } from "../data/types";
import { useLang, useT } from "../i18n";

export function useDocumentTitle(title: string): void {
  const lang = useLang();
  useEffect(() => {
    document.title = title ? `${title} · ${SITE.name}` : `${SITE.name} — ${SITE.tagline[lang]}`;
  }, [title, lang]);
}

export function ProgressBar({ value, label, tone }: { value: number; label: string; tone?: "ok" }) {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div className={`bar${tone ? ` ${tone}` : ""}`} role="progressbar" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <span style={{ width: `${v}%` }} />
    </div>
  );
}

export function ScoreRing({ percent, color, children }: { percent: number; color?: string; children: ReactNode }) {
  const style = { "--p": Math.round(percent), "--ring-color": color } as CSSProperties;
  return (
    <div className="score-ring" style={style}>
      <div>{children}</div>
    </div>
  );
}

export function ExamBadge({ examId, size = 34 }: { examId: ExamId; size?: number }) {
  const exam = EXAM_BY_ID[examId];
  return (
    <span className="badge-level" style={{ background: exam.accent, width: size, height: size }} aria-hidden="true">
      {exam.level}
    </span>
  );
}

export function Empty({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <span className="empty-icon" aria-hidden="true"><Icon size={32} strokeWidth={1.75} /></span>
      <h3>{title}</h3>
      {children}
    </div>
  );
}

const DIFF_LABEL = [["", ""], ["Cơ bản", "Basic"], ["Trung bình", "Intermediate"], ["Nâng cao", "Advanced"]] as const;
export function DifficultyChip({ level }: { level: 1 | 2 | 3 }) {
  const t = useT();
  const tone = level === 3 ? "bad" : level === 2 ? "warn" : "ok";
  const [vi, en] = DIFF_LABEL[level];
  return <span className={`chip ${tone}`}>{t(vi, en)}</span>;
}

/** Vòng xoay tải dữ liệu. */
export function Spinner({ label }: { label: string }) {
  return (
    <div className="spinner-wrap" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span className="muted small">{label}</span>
    </div>
  );
}
